import React, { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { MindItem } from '@klab-berlin/api-sdk/lib/types/responses/MindItem';
import { Product } from '@klab-berlin/api-sdk/lib/types/responses/Product';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  SxProps,
  Theme,
  Typography
} from '@mui/material';

import { selectMindItem, selectSiblings } from '../../../reducers/mindItems.selectors';
import { selectProduct } from '../../../reducers/products.selectors';
import { StoreType } from '../../../reducers';
import { customSizes, palette, spaces } from '../../../assets/styles/themes/tokens';
import { getDateInGermanFormat } from '../../../utils/format';
import { Metadata } from '../../cards/MuiCard/MuiCardBase/MuiCardBase';
import useRouter from '../../../utils/useRouter';
import { compressClassYearArray } from '../../../utils/compressClassYearArray';
import WithTruncationAnimation from '../../utils/WithTruncationAnimation';
import Tooltip from '../../Tooltip';
import getTextTruncationSx from '../../../utils/getTextTruncationSx';
import { NewAttachmentsButton } from '../../AttachmentsButton';
import { externalLinks, routes } from '../../../constants';
import { getReaderSiblingLink } from '../../../utils/pageSource';
import { ContentLicence } from '@klab-berlin/api-sdk/lib/types/common';

interface ReaderDetailsProps {
  topRightAction?: {
    icon: ReactNode;
    onClick: () => void;
    buttonSx?: SxProps<Theme>;
  };
  isContentHidden?: boolean;
  mindItemId: string;
  isPublicVideoPlayer?: boolean;
}

type MindItemProductOverview = MindItem & Partial<Product> & {
  mindItemId: string;
  mindItems: MindItem['id'][] | Product['mindItems'];
  mindItemLicence?: ContentLicence;
};

const ReaderDetails : React.FC<ReaderDetailsProps> = ({
  topRightAction,
  isContentHidden,
  mindItemId,
  isPublicVideoPlayer,
}) => {
  const { t } = useTranslation();
  const { router } = useRouter();
  const mindItem = useSelector((state: StoreType) => selectMindItem(state, mindItemId));
  const siblingMindItems =
    useSelector(state => mindItem ? selectSiblings(state, { id: mindItem.id, type: 'mindItem' }) : []);
  const product =
    useSelector((state: StoreType) => mindItem ? selectProduct(state, mindItem.product) : undefined);

  if (!mindItem) return null;
  if (!isPublicVideoPlayer && !product) return null;

  const mindItemProduct: MindItemProductOverview = {
    ...mindItem,
    ...(product ? product : {}),
    mindItemId: mindItem.id,
    mindItems: isPublicVideoPlayer ? [mindItem.id] : product!.mindItems,
    mindItemLicence: mindItem.licence,
  };

  const isVideo = mindItem.fileType === 'video';
  const hasOneVideoMindItem = isVideo && mindItemProduct.mindItems.length === 1;
  const readerHeading = isPublicVideoPlayer || hasOneVideoMindItem ? t('Video details') : t('Product details');

  const correctTitle = isPublicVideoPlayer || hasOneVideoMindItem ? mindItemProduct.subtitle : mindItemProduct.title;
  const shouldShowAttachmentsButton = !isPublicVideoPlayer && mindItemProduct.attachments.length > 0;
  const productPageHref = product?.id
    ? `/${routes.productPage.root}/${product.id}`
    : undefined;

  const renderProductDetails = () => (
    <Box
      data-testid='product-details'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: spaces.lg,
      }}
    >
      <Box sx={{
        display: 'flex',
        gap: spaces.md,
        flexDirection: 'column',
      }}>
        {
          mindItemProduct.thumbnail &&
          (product?.active && productPageHref ? (
            <Box
              component='a'
              data-testid='product-thumbnail-link'
              href={productPageHref}
              onClick={(e: React.MouseEvent) => {
                if (!e.metaKey && !e.ctrlKey) {
                  e.preventDefault();
                  router.push(productPageHref);
                }
              }}
              sx={{ display: 'inline-block' }}
            >
              <Box
                component='img'
                src={mindItemProduct.thumbnail}
                alt={mindItemProduct.title}
                sx={{
                  width: customSizes.readerDetails.publisherLogoWidth,
                  height: 'auto',
                  aspectRatio: '3 / 4',
                  objectFit: 'cover',
                  borderRadius: spaces.sm,
                  border: `1px solid ${palette.elevation.outlined}`,
                }}
              />
            </Box>
          ) : (
            <Box
              component='img'
              data-testid='product-thumbnail'
              src={mindItemProduct.thumbnail}
              alt={mindItemProduct.title}
              sx={{
                width: customSizes.readerDetails.publisherLogoWidth,
                height: 'auto',
                aspectRatio: '3 / 4',
                objectFit: 'cover',
                borderRadius: spaces.sm,
                border: `1px solid ${palette.elevation.outlined}`,
              }}
            />
          ))
        }
        {
          correctTitle && <Typography
            variant='h6'
            sx={{
              ...getTextTruncationSx(2),
            }}
          >
            {correctTitle}
          </Typography>
        }
        <MetadataReader mindItemProduct={mindItemProduct} />
      </Box>
      {shouldShowAttachmentsButton && <NewAttachmentsButton
        productId={mindItem.product}
        attachmentsCount={mindItemProduct.attachments.length}
        size='small'
      />}
    </Box>
  );

  return <Box sx={{
    display: isContentHidden ? 'none' : 'block',
    overflow: 'hidden',
    width: customSizes.readerDetails.width,
  }}>
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: customSizes.readerDetails.headerHeight,
      paddingX: spaces.md,
    }}>
      <Typography variant='overline' textTransform='uppercase'>
        {readerHeading}
      </Typography>
      {
        topRightAction && 
        <IconButton 
          onClick={topRightAction.onClick} 
          data-testid='product-details__close-button'
          aria-label={t('Close')}
          sx={{
            color: palette.text.primary,
            ...topRightAction.buttonSx,
          }}>
          {topRightAction.icon}
        </IconButton>
      }
    </Box>
    <Box sx={{
      overflowY: 'scroll',
      height: `calc(100% - ${customSizes.readerDetails.headerHeight})`,
    }}>
      <Box
        sx={{
          padding: spaces.md,
          paddingTop: 0,
          gap: spaces.xxs,
        }}
      >
        {renderProductDetails()}
      </Box>
      <Box
        sx={{
          padding: spaces.md,
          paddingBottom: spaces.sm,
        }}
        data-testid='product-details__number-of-mind-items'
      >
        <Typography variant='overline'>
          {`${mindItemProduct.mindItems.length} ${mindItemProduct.mindItems.length === 1 ? t('Unit') : t('Units')}`}
        </Typography>
      </Box>
      <AccordionReader
        mindItems={isPublicVideoPlayer ? [mindItemProduct] : siblingMindItems}
        currentMindItemId={mindItemProduct.mindItemId}
      />
    </Box>
  </Box>;
};

interface MetadataReaderProps {
  mindItemProduct: MindItemProductOverview;
}

const MetadataReader: React.FC<MetadataReaderProps> = ({ mindItemProduct }) => {
  const { t } = useTranslation();
  const date = mindItemProduct.publisherReleaseDate && getDateInGermanFormat(mindItemProduct.publisherReleaseDate);
  const metadataStyles = { flex: '1 0 0' };
  const hasSubjects = mindItemProduct.subjects.length > 0;
  const hasClassYears = mindItemProduct.classYears.length > 0;
  const licence = mindItemProduct.mindItemLicence;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: spaces.md,
      }}
    >
      {
        (hasSubjects || hasClassYears) &&
      <MetadataReaderRow>
        <Tooltip label={mindItemProduct.subjects.join(', ')} placement='top-start'>
          <Metadata
            sx={metadataStyles}
            icon='tag'
            label={mindItemProduct.subjects.join(', ')}
          />
        </Tooltip>
        <Tooltip label={compressClassYearArray(mindItemProduct.classYears).join(', ')} placement='top-start'>
          <Metadata 
            sx={metadataStyles}
            icon='teacher'
            label={compressClassYearArray(mindItemProduct.classYears).join(', ') + ' ' + t('Class year')}
          />
        </Tooltip>
      </MetadataReaderRow>
      }
      {
        (mindItemProduct.publisher || date) &&
      <MetadataReaderRow>
        {
          mindItemProduct.publisher &&
          <Tooltip label={mindItemProduct.publisher} placement='top-start'>
            <Metadata
              sx={metadataStyles}
              icon='publisher'
              label={mindItemProduct.publisher}
            />
          </Tooltip>
        }
        {
          date && 
          <Tooltip label={date} placement='top-start'>
            <Metadata
              sx={metadataStyles}
              icon='calendar'
              label={date}
            />
          </Tooltip>
        }
      </MetadataReaderRow>
      }
      {licence && (
        <Tooltip label={licence} placement='top-start'>
          <Metadata
            sx={{
              ...metadataStyles,
              color: palette.primary.main,
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
            icon='licence'
            label={licence}
            onClick={(e) => {
              e.stopPropagation();
              const link =
                externalLinks.licenceLinks[licence as ContentLicence];
              if (link) window.open(link, '_blank');
            }}
            role='link'
          />
        </Tooltip>
      )}
    </Box>
  );
};

const AccordionReader: React.FC<{
  mindItems: MindItem[] | MindItemProductOverview[],
  currentMindItemId: string,
}> = ({ mindItems, currentMindItemId }) => {
  const { router } = useRouter();
  const { t } = useTranslation();
  const refs = React.useRef<Record<string, HTMLButtonElement | null>>({});

  useEffect(() => {
    const button = refs.current[currentMindItemId];
    if (currentMindItemId && button) {
      button.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentMindItemId]);

  return <Box sx={{ paddingX: spaces.md }} data-testid='accordion-reader'>
    {mindItems.map((mindItem, index) => {
      const isCurrentMindItem = mindItem.id === currentMindItemId;

      const getToggleButton = (isTruncated: boolean) => <Button
        variant='text'
        size='small'
        color='secondary'
      >
        {isTruncated ? t('Show more') : t('Show less')}
      </Button>;

      return <Accordion
        key={mindItem.id}
        expanded={isCurrentMindItem}
        onChange={() => {
          if (!isCurrentMindItem) {
            router.push(getReaderSiblingLink(mindItem.id));
          }
        }}
        sx={{
          margin: '0 !important',
          '::before': {
            display: 'none',
          },
        }}
        elevation={0}
      >
        <AccordionSummary 
          sx={{
            padding: 0,
            margin: 0,
            '& .MuiAccordionSummary-content': {
              margin: '0 !important',
            },
            '&.Mui-expanded': {
              minHeight: 'unset',
            },
          }}
        >
          <Button
            variant='text'
            size='large'
            data-testid={`accordion-reader-button-${index + 1}`}
            sx={{
              width: '100%',
              justifyContent: 'flex-start',
              textAlign: 'left',
              ...(isCurrentMindItem ? {
                backgroundColor: palette.primary.hover
              } : {}),
            }}
            ref={el => refs.current[mindItem.id] = el}
          >
            {`${index + 1}. ${mindItem.title}`}
          </Button>
        </AccordionSummary>
        <AccordionDetails sx={{
          paddingY: spaces.xs,
          display: 'flex',
          flexDirection: 'column',
          gap: spaces.xs,
          paddingLeft: spaces.xxs,
          alignItems: 'flex-start',
        }}>
          <WithTruncationAnimation
            maxLines={7}
            renderToggleButton={getToggleButton}
          >
            <Typography
              variant='body2'
              sx={{
                paddingX: spaces.xs,
              }}
            >
              {mindItem.description}
            </Typography>
          </WithTruncationAnimation>
        </AccordionDetails>
      </Accordion>;
    })}
  </Box>;
};

const MetadataReaderRow: React.FC = (props) => {
  return <Box sx={{
    display: 'flex',
    gap: spaces.md,
  }}>
    {
      Array.isArray(props.children) && 
      props.children.map((child, index) => child && <Box key={index} sx={{ flex: 1 }}>{child}</Box>)
    }
  </Box>;
};

export default ReaderDetails;
